import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, InputNumber, DatePicker, Select, Space, message, Tag, Popconfirm, Row, Col, TimePicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { courseAPI } from '../../services/api';
import dayjs from 'dayjs';
import { Course } from '../../types';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const CourseManagement: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const response = await courseAPI.getCourses();
      setCourses(response.courses);
    } catch (error) {
      message.error('获取课程列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCourse = () => {
    setEditingCourse(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
    
    // Map difficulty level from integer to Chinese string
    const difficultyMap: { [key: number]: string } = {
      1: '一星',
      2: '二星',
      3: '三星',
      4: '四星',
      5: '五星'
    };
    
    const difficultyLevel = typeof course.difficultyLevel === 'number' 
      ? difficultyMap[course.difficultyLevel] || '一星'
      : (typeof course.difficultyLevel === 'string' ? course.difficultyLevel : '一星');
    
    console.log('Editing course:', course);
    console.log('Setting difficulty level:', difficultyLevel);
    console.log('Setting semester:', course.semester);
    
    form.setFieldsValue({
      name: course.name,
      teacher_name: course.teacherName,
      category: course.category,
      semester: course.semester,
      grade_level: course.gradeLevel,
      difficulty_level: difficultyLevel,
      location: course.location,
      start_date: course.startDate ? dayjs(course.startDate) : null,
      end_date: course.endDate ? dayjs(course.endDate) : null,
      total_lessons: course.totalLessons,
      class_time_description: course.classTimeDescription || '',
      price: course.price,
      max_students: course.maxStudents,
      description: course.description,
      image_url: course.imageUrl || ''
    });
    setModalVisible(true);
  };

  const handleDeleteCourse = async (id: number) => {
    try {
      await courseAPI.deleteCourse(id);
      message.success('删除成功');
      fetchCourses();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // Map difficulty level from Chinese string to integer
      const difficultyMap: { [key: string]: number } = {
        '一星': 1,
        '二星': 2,
        '三星': 3,
        '四星': 4,
        '五星': 5
      };

      // Ensure semester is a string
      const semester = String(values.semester || '');
      
      // Ensure difficulty level is a number
      const difficultyLevel = difficultyMap[values.difficulty_level] || 
        (typeof values.difficulty_level === 'number' ? values.difficulty_level : 1);

      const courseData = {
        name: String(values.name || ''),
        teacherName: String(values.teacher_name || ''),
        category: String(values.category || ''),
        semester: semester,
        gradeLevel: String(values.grade_level || ''),
        difficultyLevel: difficultyLevel,
        location: String(values.location || ''),
        startDate: values.start_date?.format('YYYY-MM-DD'),
        endDate: values.end_date?.format('YYYY-MM-DD'),
        totalLessons: Number(values.total_lessons) || 0,
        classTimeDescription: String(values.class_time_description || ''),
        price: Number(values.price) || 0,
        maxStudents: Number(values.max_students) || 0,
        description: String(values.description || ''),
        imageUrl: String(values.image_url || '')
      };
      
      console.log('Submitting course data:', courseData);

      if (editingCourse) {
        await courseAPI.updateCourse(editingCourse.id, courseData);
        message.success('更新成功');
      } else {
        await courseAPI.createCourse(courseData);
        message.success('创建成功');
      }

      setModalVisible(false);
      fetchCourses();
    } catch (error: any) {
      console.error('Error submitting course:', error);
      message.error(editingCourse ? '更新失败: ' + (error.response?.data?.message || error.message) : '创建失败: ' + (error.response?.data?.message || error.message));
    }
  };

  const columns = [
    {
      title: '课程名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: '教师',
      dataIndex: 'teacherName',
      key: 'teacherName',
      width: 100,
    },
    {
      title: '学期',
      dataIndex: 'semester',
      key: 'semester',
      width: 80,
      render: (semester: string) => (
        <Tag color="green">{semester}</Tag>
      ),
    },
    {
      title: '年级',
      dataIndex: 'gradeLevel',
      key: 'gradeLevel',
      width: 80,
      render: (gradeLevel: string) => (
        <Tag color="blue">{gradeLevel}</Tag>
      ),
    },
    {
      title: '难度',
      dataIndex: 'difficultyLevel',
      key: 'difficultyLevel',
      width: 80,
      render: (difficultyLevel: number | string) => {
        const difficultyMap: { [key: number]: string } = {
          1: '一星',
          2: '二星',
          3: '三星',
          4: '四星',
          5: '五星'
        };
        const level = typeof difficultyLevel === 'number' ? difficultyLevel : parseInt(difficultyLevel as string) || 1;
        return <Tag color="orange">{difficultyMap[level] || '一星'}</Tag>;
      },
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 80,
      render: (category: string) => (
        <Tag color="purple">{category}</Tag>
      ),
    },
    {
      title: '价格',
      dataIndex: 'price',
      key: 'price',
      width: 80,
      render: (price: number) => `¥${price}`,
    },
    {
      title: '时间范围',
      key: 'dateRange',
      width: 150,
      render: (_: any, record: Course) => (
        <span>
          {record.startDate && record.endDate 
            ? `${dayjs(record.startDate).format('MM-DD')} 至 ${dayjs(record.endDate).format('MM-DD')}`
            : '时间待定'
          }
        </span>
      ),
    },
    {
      title: '课时',
      key: 'lessons',
      width: 80,
      render: (_: any, record: Course) => (
        <span>{record.totalLessons}节</span>
      ),
    },
    {
      title: '人数',
      key: 'students',
      width: 100,
      render: (_: any, record: Course) => (
        <span>{record.currentStudents}/{record.maxStudents}</span>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => {
        const colors = { '正常': 'green', '转出': 'orange', '过期': 'red', '退课': 'red' };
        return <Tag color={colors[status as keyof typeof colors]}>{status}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: Course) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditCourse(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个课程吗？"
            onConfirm={() => handleDeleteCourse(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>课程管理</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddCourse}
        >
          添加课程
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={courses}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title={editingCourse ? '编辑课程' : '添加课程'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            difficulty_level: '一星',
            total_lessons: 1,
            class_time_description: '',
            price: 0,
            max_students: 20
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="课程名称"
                name="name"
                rules={[{ required: true, message: '请输入课程名称' }]}
              >
                <Input placeholder="请输入课程名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="教师姓名"
                name="teacher_name"
                rules={[{ required: true, message: '请输入教师姓名' }]}
              >
                <Input placeholder="请输入教师姓名" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="课程分类"
                name="category"
                rules={[{ required: true, message: '请选择课程分类' }]}
              >
                <Select placeholder="请选择课程分类">
                  <Option value="语文">语文</Option>
                  <Option value="数学">数学</Option>
                  <Option value="英语">英语</Option>
                  <Option value="写作">写作</Option>
                  <Option value="阅读">阅读</Option>
                  <Option value="口语">口语</Option>
                  <Option value="其他">其他</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="上课地点"
                name="location"
                rules={[{ required: true, message: '请输入上课地点' }]}
              >
                <Input placeholder="请输入上课地点" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="学期"
                name="semester"
                rules={[{ required: true, message: '请选择学期' }]}
              >
                <Select placeholder="请选择学期">
                  <Option value="寒假">寒假</Option>
                  <Option value="暑假">暑假</Option>
                  <Option value="春季">春季</Option>
                  <Option value="秋季">秋季</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="年级"
                name="grade_level"
                rules={[{ required: true, message: '请选择年级' }]}
              >
                <Select placeholder="请选择年级">
                  <Option value="四年级">四年级</Option>
                  <Option value="五年级">五年级</Option>
                  <Option value="六年级">六年级</Option>
                  <Option value="初一">初一</Option>
                  <Option value="初二">初二</Option>
                  <Option value="初三">初三</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="难度等级"
                name="difficulty_level"
                rules={[{ required: true, message: '请选择难度等级' }]}
              >
                <Select placeholder="请选择难度等级" allowClear={false}>
                  <Option value="一星">一星</Option>
                  <Option value="二星">二星</Option>
                  <Option value="三星">三星</Option>
                  <Option value="四星">四星</Option>
                  <Option value="五星">五星</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="上课起止时间"
                name="class_time_description"
              >
                <Input placeholder="如：上午9:00-11:00" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="开始日期"
                name="start_date"
                rules={[{ required: true, message: '请选择开始日期' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="结束日期"
                name="end_date"
                rules={[{ required: true, message: '请选择结束日期' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="总课时"
                name="total_lessons"
                rules={[{ required: true, message: '请输入总课时' }]}
              >
                <InputNumber min={1} max={50} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="价格(元)"
                name="price"
                rules={[{ required: true, message: '请输入价格' }]}
              >
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="最大人数"
                name="max_students"
                rules={[{ required: true, message: '请输入最大人数' }]}
              >
                <InputNumber min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="课程描述"
            name="description"
            rules={[{ required: false, message: '请输入课程描述' }]}
          >
            <TextArea rows={4} placeholder="请输入课程详细描述（选填）" />
          </Form.Item>

          <Form.Item
            label="图片URL"
            name="image_url"
          >
            <Input placeholder="请输入课程图片URL" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingCourse ? '更新课程' : '创建课程'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CourseManagement;